const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HEX_ID_PATTERN = /^[0-9a-f]{16,}$/i;
const OPAQUE_ID_PATTERN = /^(?=.*[a-z])(?=.*\d)[a-z0-9_-]{20,}$/i;
const KLEKSI_SHORT_ID_PATTERN = /^(?=.*[a-z])(?=.*\d)[a-z0-9]{6}$/i;
const STATIC_ID_LIKE_SEGMENTS = new Set(['oauth2']);
const DASHBOARD_DYNAMIC_PARENTS = new Set([
  'applications-overview',
  'batch-edit-history',
  'edit',
  'export',
  'file-manager',
  'fill',
  'images',
  'restore',
  'statistics',
  'template',
  'users-overview',
  'view',
]);
const DASHBOARD_STATIC_CHILDREN: Readonly<Record<string, ReadonlySet<string>>> = {
  'users-overview': new Set(['add']),
};

/**
 * API collections whose next segment identifies one resource unless it is a
 * known static child route.
 */
const API_RESOURCE_COLLECTIONS = new Set([
  'applications',
  'batches',
  'bundles',
  'collectionitems',
  'collections',
  'comments',
  'datasets',
  'fields',
  'fieldroots',
  'fieldtemplates',
  'fieldvalues',
  'files',
  'folders',
  'imports',
  'installations',
  'items',
  'licenses',
  'lists',
  'members',
  'memberships',
  'membersystems',
  'notifications',
  'operations',
  'permissions',
  'procedures',
  'registers',
  'roles',
  'sessions',
  'templates',
  'terms',
  'users',
  'valueroots',
  'values',
  'versions',
]);

const API_STATIC_COLLECTION_CHILDREN: Readonly<Record<string, ReadonlySet<string>>> = {
  applications: new Set(['search']),
  batches: new Set(['definitions']),
  collectionitems: new Set(['batches', 'exports', 'header', 'imports', 'lists', 'search']),
  comments: new Set(['challenge', 'search']),
  fields: new Set(['search']),
  fieldroots: new Set(['positions', 'search']),
  files: new Set(['cropper', 'extract', 'multipart', 'operations', 'processes', 'search']),
  folders: new Set(['merge-migration', 'root', 'search']),
  members: new Set(['callback', 'login', 'logout', 'reindex', 'search']),
  notifications: new Set(['archive', 'read', 'reconcile', 'unread']),
  roles: new Set(['settings']),
  templates: new Set(['search']),
  terms: new Set(['datasets', 'predicates', 'search', 'types']),
  users: new Set(['search']),
};

export function normalizePath(pathname: string): string {
  return pathname.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
}

export function canonicalizeDashboardRoute(pathnameOrTemplate: string): string {
  return mapPathSegments(pathnameOrTemplate, (segments, index, segment) => {
    if (isRouteParameter(segment) || isDashboardResourceId(segments, index)) {
      return ':guid';
    }

    return isDynamicSegment(segment) ? ':guid' : segment;
  });
}

export function canonicalizeOwnedApiRoute(pathname: string): string {
  return mapPathSegments(pathname, (segments, index, segment) => {
    if (isRouteParameter(segment) || isApplicationId(segments, index) || isApiResourceId(segments, index)) {
      return ':guid';
    }

    return isDynamicSegment(segment) ? ':guid' : segment;
  });
}

function mapPathSegments(
  pathname: string,
  mapSegment: (segments: string[], index: number, segment: string) => string,
): string {
  const segments = normalizePath(stripQueryAndFragment(pathname)).split('/');
  return segments.map((segment, index) => mapSegment(segments, index, segment)).join('/');
}

function stripQueryAndFragment(pathname: string): string {
  return pathname.split('?')[0].split('#')[0];
}

function isApplicationId(segments: string[], index: number): boolean {
  return index > 0 && segments[index - 1].toLowerCase() === 'apps';
}

function isDashboardResourceId(segments: string[], index: number): boolean {
  if (index === 0) {
    return false;
  }

  const parent = segments[index - 1].toLowerCase();
  return DASHBOARD_DYNAMIC_PARENTS.has(parent)
    && !DASHBOARD_STATIC_CHILDREN[parent]?.has(segments[index].toLowerCase());
}

function isApiResourceId(segments: string[], index: number): boolean {
  if (index === 0) {
    return false;
  }

  const collection = segments[index - 1].toLowerCase();
  if (!API_RESOURCE_COLLECTIONS.has(collection)) {
    return false;
  }

  return !API_STATIC_COLLECTION_CHILDREN[collection]?.has(segments[index].toLowerCase());
}

function isDynamicSegment(segment: string): boolean {
  return /^\d+$/.test(segment)
    || /@|%40/i.test(segment)
    || UUID_PATTERN.test(segment)
    || HEX_ID_PATTERN.test(segment)
    || OPAQUE_ID_PATTERN.test(segment)
    || (KLEKSI_SHORT_ID_PATTERN.test(segment) && !STATIC_ID_LIKE_SEGMENTS.has(segment.toLowerCase()));
}

function isRouteParameter(segment: string): boolean {
  return /^(?::[^/]+|\{[^/]+\})$/.test(segment);
}
